import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const EditorialPrinciplesPage = () => (
  <StaticPageLayout
    overline="Journals"
    title="Editorial Principles"
    description="The editorial principles that guide how MAH Quantum Research Institute selects, evaluates, and publishes research across all its journals."
    breadcrumbs={[
      { label: 'Journals', path: '/journals' },
      { label: 'Editorial Principles', path: '/editorial-principles' },
    ]}
  >
    <div className="space-y-10">
      <div className="bg-blue-50 border border-blue-200 px-5 py-4">
        <p className="text-sm text-[#174EA6] leading-relaxed">
          These principles apply uniformly across all journals published under the MAH Quantum Research Institute platform. They govern editorial decision-making, reviewer conduct, and the standards of scholarship we uphold.
        </p>
      </div>

      {/* Independence & Integrity */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Editorial Independence
        </h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          All editorial decisions — including acceptance, revision, and rejection — are made solely on the basis of scholarly merit, originality, and relevance to the scope of the journal. No commercial, institutional, or personal interest influences these decisions.
        </p>
        <div className="space-y-3">
          {[
            {
              title: 'Freedom from External Pressure',
              desc: 'Editors are free to make decisions independently of the interests of journal owners, advertisers, or any third party. Submissions from authors affiliated with the institute are held to the same standards as all other submissions.',
            },
            {
              title: 'Separation of Editorial and Business Functions',
              desc: 'The editorial team operates independently from the administrative and business functions of the institute. Revenue considerations do not enter into any editorial determination.',
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-5 bg-white">
              <h3 className="font-semibold text-[#1F1F1F] text-sm mb-2">{item.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Scope & Selection */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Scope and Article Selection
        </h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Each journal under MAH Quantum Research Institute has a defined scope published on its individual journal page. Submissions are evaluated first for scope fit, then for scientific or scholarly quality. The following criteria guide selection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Originality', desc: 'Work must represent a meaningful, original contribution to the field.' },
            { label: 'Scientific Rigour', desc: 'Methodology must be sound, reproducible, and clearly described.' },
            { label: 'Significance', desc: 'Results must advance knowledge or challenge existing understanding in a meaningful way.' },
            { label: 'Clarity of Presentation', desc: 'Manuscripts must be written with clarity and conform to the journal\'s formatting requirements.' },
            { label: 'Ethical Compliance', desc: 'Research involving human subjects, animals, or sensitive data must comply with relevant ethical standards.' },
            { label: 'Transparency of Data', desc: 'Data, methods, and materials must be available for scrutiny to the extent possible within applicable constraints.' },
          ].map(item => (
            <div key={item.label} className="border border-[#E5E7EB] p-4 bg-white">
              <p className="font-semibold text-[#1F1F1F] text-sm mb-1">{item.label}</p>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Peer Review */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Peer Review Standards
        </h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          All research articles undergo double-blind peer review before acceptance. Reviewers are selected based on their demonstrated expertise in the relevant sub-field and must not have a conflict of interest with the submitting authors.
        </p>
        <div className="space-y-3">
          {[
            {
              title: 'Confidentiality',
              desc: 'All manuscripts under review are treated as confidential documents. Reviewers must not disclose, share, or use any content of a submission before publication.',
            },
            {
              title: 'Constructive Feedback',
              desc: 'Reviewers are expected to provide substantive, respectful, and actionable feedback. Reviews that are dismissive, hostile, or unsubstantiated are not accepted.',
            },
            {
              title: 'Timeliness',
              desc: 'Reviewers are expected to complete reviews within the agreed timeframe. Delays must be communicated to the editorial office promptly.',
            },
            {
              title: 'Conflict of Interest Disclosure',
              desc: 'Reviewers who identify a potential conflict of interest — professional, personal, or financial — must declare this immediately and recuse themselves from the review.',
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-5 bg-white">
              <h3 className="font-semibold text-[#1F1F1F] text-sm mb-2">{item.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Editorial Decisions */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Editorial Decision-Making
        </h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Editorial decisions are communicated clearly and promptly. Authors are entitled to understand the basis of any decision affecting their submission.
        </p>
        <div className="space-y-3">
          {[
            {
              title: 'Accept',
              desc: 'The manuscript meets all criteria and is accepted for publication as submitted or with only minor typesetting corrections.',
            },
            {
              title: 'Minor Revision',
              desc: 'The manuscript is of acceptable quality but requires specific, limited changes before it can be accepted. Revised manuscripts are typically reviewed by the handling editor only.',
            },
            {
              title: 'Major Revision',
              desc: 'The manuscript shows potential but requires substantial revisions in scope, methodology, or presentation. Revised submissions are returned to peer reviewers.',
            },
            {
              title: 'Reject',
              desc: 'The manuscript does not meet the standards of the journal, falls outside scope, or has irremediable methodological or ethical issues. Authors may be invited to resubmit to a more suitable journal under the institute.',
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-5 bg-white">
              <h3 className="font-semibold text-[#1F1F1F] text-sm mb-2">{item.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Corrections & Retractions */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Corrections, Expressions of Concern, and Retractions
        </h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Scientific integrity requires that errors be corrected transparently and in a timely manner. The following policies apply to all published articles:
        </p>
        <div className="space-y-3">
          {[
            {
              title: 'Corrections',
              desc: 'Where a published article contains factual errors that do not affect the validity of conclusions, a formal correction notice will be published and linked to the original article.',
            },
            {
              title: 'Expressions of Concern',
              desc: 'Where there are reasonable grounds to believe that an article may be unreliable but a full investigation has not concluded, an expression of concern will be issued pending outcome.',
            },
            {
              title: 'Retractions',
              desc: 'Articles found to contain fabricated data, plagiarised content, or fundamental methodological flaws that invalidate conclusions will be formally retracted with a public notice explaining the reason.',
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-5 bg-white">
              <h3 className="font-semibold text-[#1F1F1F] text-sm mb-2">{item.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Diversity & Inclusion */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Diversity, Equity, and Inclusion
        </h2>
        <p className="text-[#4B5563] leading-relaxed">
          MAH Quantum Research Institute is committed to building an editorial ecosystem that is inclusive of researchers across all geographies, career stages, and institutional backgrounds. Reviewer and editorial board recruitment actively seeks geographic and disciplinary diversity. Submissions are evaluated blind to author identity, affiliation, and country of origin.
        </p>
      </section>

      {/* Contact */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
          Editorial Enquiries
        </h2>
        <p className="text-[#4B5563] leading-relaxed">
          Enquiries regarding editorial policies, decisions, or appeals should be directed to the editorial office at{' '}
          <span className="text-[#1A73E8]">info@mahquantum.tech</span>. Authors wishing to appeal a rejection decision should consult the{' '}
          <a href="/rejection-appeals" className="text-[#1A73E8] hover:underline">Rejection &amp; Appeals</a> policy page.
        </p>
      </section>
    </div>
  </StaticPageLayout>
);

export default EditorialPrinciplesPage;
